import { FC, useMemo } from 'react';

import { useRecoilState } from 'recoil';

import { BugoBrand } from '@shared/api/bugoBrand/bugoBrand.interface';
import {
  MonthlyAccountedItem,
  MonthlyAccountedSummary,
} from '@shared/api/needAccount/needAccount.interface';
import { useAuth } from '@shared/state/hooks/useAuth';
import { getRebateDate } from '@shared/utils/dateUtil';
import { formatDate } from '@shared/utils/formatDate';
import { DatePicker, Table, TableColumnsType } from 'antd';
import locale from 'antd/es/date-picker/locale/ko_KR';
import _ from 'lodash';

import { AgencyAdminAccountDetailModalButton } from '../AgencyAdminAccountDetail/AgencyAdminAccountDetailModalButton';
import { agencyAdminAccountSelectYearAtom } from './AgencyAdminAccount.atom';

//* 연간 정산 완료 내역
interface Props {
  monthlyAccounted: MonthlyAccountedItem[];
  summary: MonthlyAccountedSummary;
  selectedYear: number;
}

type AccountData = {
  id: string;
  month: number;
  accountedDate: string | null;
  rebateAgencyPrice: number;
  rebateWorkerPrice: number;
  rebateCount: number;
  needAccountCount: number;
  feventCount: number;
  orderedFeventCount: number;
};

const columns: (
  bugoBrand?: BugoBrand,
  selectedYear?: number,
) => TableColumnsType<AccountData> = (bugoBrand?: BugoBrand, selectedYear?: number) => {
  if (bugoBrand?.bugoAgency?.shareRebateWithWorker) {
    return [
      {
        title: '정산기간',
        dataIndex: 'month',
        key: 'month',
        align: 'center',
        render: (month: number) => {
          return <p className="px-6 py-1 text-base font-medium">{month}월</p>;
        },
      },
      {
        title: '정산일',
        dataIndex: 'accountedDate',
        key: 'accountedDate',
        align: 'center',
        render: (accountedDate: string | null, record: AccountData) => {
          if (accountedDate) {
            return (
              <p className="px-6 py-1 text-base font-medium text-green-600">
                {formatDate(new Date(accountedDate))} 완료
              </p>
            );
          } else {
            const rebateDate = getRebateDate(record.month, selectedYear);
            const now = new Date();
            if (record.rebateCount > 0 && now.getTime() > rebateDate.getTime()) {
              return (
                <p className="px-6 py-1 text-base font-medium theme-text-warn-03">
                  {formatDate(rebateDate)} 미지급
                </p>
              );
            } else {
              return (
                <p className="px-6 py-1 text-base font-medium">
                  {formatDate(rebateDate)} 예정
                </p>
              );
            }
          }
        },
      },
      {
        title: '부고건수',
        dataIndex: 'feventCount',
        key: 'feventCount',
        align: 'center',
        render: (_: number, record: AccountData) => {
          if (record.feventCount === 0 && record.orderedFeventCount === 0) {
            return <p className="px-6 py-1 text-base">-</p>;
          }
          return (
            <p className="px-6 py-1 text-base font-medium">
              {record.orderedFeventCount}/{record.feventCount}
            </p>
          );
        },
      },
      {
        title: '판매건수(정산/총)',
        dataIndex: 'rebateCount',
        key: 'rebateCount',
        align: 'center',
        render: (rebateCount: number, record: AccountData) => {
          const totalSales = rebateCount + (record.needAccountCount ?? 0);
          if (totalSales === 0) {
            return <p className="px-6 py-1 text-base">-</p>;
          }
          return (
            <p className="px-6 py-1 text-base font-medium">
              {rebateCount.toLocaleString()}/{totalSales.toLocaleString()}
            </p>
          );
        },
      },
      {
        title: bugoBrand.bugoAgency.nickName + ' 정산금',
        dataIndex: 'rebateAgencyPrice',
        key: 'rebateAgencyPrice',
        align: 'center',
        render: (rebateAgencyPrice: number) => {
          return (
            <p className="px-6 py-1 text-base font-medium">
              {rebateAgencyPrice === 0 ? '-' : rebateAgencyPrice.toLocaleString() + '원'}
            </p>
          );
        },
      },
      {
        title: '지도사 정산금',
        dataIndex: 'rebateWorkerPrice',
        key: 'rebateWorkerPrice',
        align: 'center',
        render: (rebateWorkerPrice: number) => {
          return (
            <p className="px-6 py-1 text-base font-medium">
              {rebateWorkerPrice === 0 ? '-' : rebateWorkerPrice.toLocaleString() + '원'}
            </p>
          );
        },
      },
      {
        title: '합계',
        dataIndex: 'id',
        key: 'total',
        align: 'center',
        render: (_, record: AccountData) => {
          return (
            <p className="px-6 py-1 text-base font-medium">
              {record.rebateAgencyPrice + record.rebateWorkerPrice === 0
                ? '-'
                : (record.rebateAgencyPrice + record.rebateWorkerPrice).toLocaleString() +
                  '원'}
            </p>
          );
        },
      },
      {
        title: '세부내역보기',
        dataIndex: 'id',
        key: 'detail',
        align: 'center',
        render: (_, record: AccountData) => {
          return (
            <div className="center-box">
              <AgencyAdminAccountDetailModalButton
                year={selectedYear ?? new Date().getFullYear()}
                month={record.month}
                type="accounted"
              />
            </div>
          );
        },
      },
    ];
  } else {
    return [
      {
        title: '정산기간',
        dataIndex: 'month',
        key: 'month',
        align: 'center',
        render: (month: number) => {
          return <p className="px-6 py-1 text-base font-medium">{month}월</p>;
        },
      },
      {
        title: '정산일',
        dataIndex: 'accountedDate',
        key: 'accountedDate',
        align: 'center',
        render: (accountedDate: string | null, record: AccountData) => {
          if (accountedDate) {
            return (
              <p className="px-6 py-1 text-base font-medium text-green-600">
                {formatDate(new Date(accountedDate))} 완료
              </p>
            );
          } else {
            const rebateDate = getRebateDate(record.month, selectedYear);
            const now = new Date();
            if (record.rebateCount > 0 && now.getTime() > rebateDate.getTime()) {
              return (
                <p className="px-6 py-1 text-base font-medium theme-text-warn-03">
                  {formatDate(rebateDate)} 미지급
                </p>
              );
            } else {
              return (
                <p className="px-6 py-1 text-base font-medium">
                  {formatDate(rebateDate)} 예정
                </p>
              );
            }
          }
        },
      },
      {
        title: '부고건수',
        dataIndex: 'feventCount',
        key: 'feventCount',
        align: 'center',
        render: (_: number, record: AccountData) => {
          if (record.feventCount === 0 && record.orderedFeventCount === 0) {
            return <p className="px-6 py-1 text-base">-</p>;
          }
          return (
            <p className="px-6 py-1 text-base font-medium">
              {record.orderedFeventCount}/{record.feventCount}
            </p>
          );
        },
      },
      {
        title: '판매건수(정산/총)',
        dataIndex: 'rebateCount',
        key: 'rebateCount',
        align: 'center',
        render: (rebateCount: number, record: AccountData) => {
          const totalSales = rebateCount + (record.needAccountCount ?? 0);
          if (totalSales === 0) {
            return <p className="px-6 py-1 text-base">-</p>;
          }
          return (
            <p className="px-6 py-1 text-base font-medium">
              {rebateCount.toLocaleString()}/{totalSales.toLocaleString()}
            </p>
          );
        },
      },
      {
        title: bugoBrand?.bugoAgency?.nickName ?? '' + ' 정산금',
        dataIndex: 'rebateAgencyPrice',
        key: 'rebateAgencyPrice',
        align: 'center',
        render: (rebateAgencyPrice: number) => {
          return (
            <p className="px-6 py-1 text-base font-medium">
              {rebateAgencyPrice === 0 ? '-' : rebateAgencyPrice.toLocaleString() + '원'}
            </p>
          );
        },
      },
      {
        title: '세부내역보기',
        dataIndex: 'id',
        key: 'detail',
        align: 'center',
        render: (_, record: AccountData) => {
          return (
            <div className="center-box">
              <AgencyAdminAccountDetailModalButton
                year={selectedYear ?? new Date().getFullYear()}
                month={record.month}
                type="accounted"
              />
            </div>
          );
        },
      },
    ];
  }
};

export const AnnualAccountedTable: FC<Props> = function AnnualAccountedTable({
  monthlyAccounted,
  summary,
  selectedYear,
}: Props) {
  const { userProfile } = useAuth();
  const bugoBrand = userProfile?.bugoAgencyAdminDetail?.bugoBrands[0];

  const [selectYear, setSelectYear] = useRecoilState(agencyAdminAccountSelectYearAtom);

  const tableData: AccountData[] = useMemo(() => {
    return _.map(monthlyAccounted, (item) => {
      return {
        id: `accountTable-${item.month}`,
        month: item.month,
        accountedDate: item.accountedDate,
        rebateCount: item.totalCount,
        needAccountCount: item.needAccountCount ?? 0,
        rebateAgencyPrice: item.totalAgencyIncome,
        rebateWorkerPrice: item.totalWorkerIncome,
        feventCount: item.feventCount ?? 0,
        orderedFeventCount: item.orderedFeventCount ?? 0,
      };
    });
  }, [monthlyAccounted]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between pr-4">
        <p className="pl-4 text-base font-medium">
          {selectYear?.format('YYYY년')} 연간 정산완료 내역
        </p>
        <DatePicker
          picker="year"
          locale={locale}
          size="large"
          defaultValue={selectYear ?? undefined}
          onChange={(date) => {
            setSelectYear(date);
          }}
        />
      </div>
      <Table
        className="w-full"
        columns={columns(bugoBrand, selectedYear)}
        dataSource={_.flatMapDeep([tableData])}
        pagination={{
          defaultPageSize: 13,
          hideOnSinglePage: true,
        }}
        rowKey={'id'}
        bordered
        size={'small'}
      />
      <div className="button-rectangle flex cursor-default items-center justify-evenly gap-4 rounded-lg text-base theme-text-4 theme-bg-12">
        <p>합산</p>
        <p>{summary.totalCount}건</p>
        <p>{summary.totalAgencyIncome.toLocaleString()}원</p>
        {bugoBrand?.bugoAgency?.shareRebateWithWorker && (
          <>
            <p>{summary.totalWorkerIncome.toLocaleString()}원</p>
            <p>
              {(summary.totalAgencyIncome + summary.totalWorkerIncome).toLocaleString()}원
            </p>
          </>
        )}
      </div>
    </div>
  );
};
